// src/features/resources/components/FolderSelector.js
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { createFolder, fetchFoldersForUser } from "../../services/folderService";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Props:
 * - value: selected folder id
 * - onChange: fn(folderId)
 * - allowCreate: boolean
 */
const FolderSelector = ({ value, onChange, allowCreate = true }) => {
  const { currentUser, userProfile } = useAuth();
  const [folders, setFolders] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [visibility, setVisibility] = useState("private"); // default

  useEffect(() => {
    const load = async () => {
      const all = await fetchFoldersForUser(userProfile);
      setFolders(all || []);
    };
    load();
  }, [userProfile]);

  const handleCreate = async () => {
    if (!folderName.trim() || !currentUser) return;
    const folder = {
      name: folderName.trim(),
      visibility,
      ownerId: currentUser.uid,
      institutionId: userProfile?.institutionId || null
    };
    await createFolder(folder);
    setOpenCreate(false);
    setFolderName("");
    // Reload folders
    const all = await fetchFoldersForUser(userProfile);
    setFolders(all || []);
  };

  return (
    <Box>
      <TextField
        select
        label="Folder"
        fullWidth
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
      >
        <MenuItem value="">— Select folder —</MenuItem>
        {folders.map(f => (
          <MenuItem key={f.id} value={f.id}>
            {f.name} — {f.visibility}
          </MenuItem>
        ))}
      </TextField>

      {allowCreate && (
        <>
          <Button size="small" variant="outlined" onClick={() => setOpenCreate(true)}>
            Create Folder
          </Button>

          <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogContent>
              <TextField
                label="Folder name"
                fullWidth
                value={folderName}
                onChange={e => setFolderName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                select
                label="Visibility"
                fullWidth
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                size="small"
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="institutional">Institutional</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </TextField>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                Public folders are visible to all users. Institutional folders are visible only to users in the same institution.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate}>Create</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default FolderSelector;
